import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom'
import { useAdminAuth } from '../hooks/useAdminAuth.jsx'
import AdminHeader from './AdminHeader'
import AssessmentsList from './AssessmentsList'
import AssessmentDetail from './AssessmentDetail'
import ComparisonPage from './ComparisonPage'
import DashboardStats from './DashboardStats'
import AdminTestPanel from './AdminTestPanel'
import QuestionnaireManager from './QuestionnaireManager'
import UserManagement from './UserManagement'
import ProtectedRoute from './ProtectedRoute'
import { assessmentApiUrl } from '../utils/adminLinks'

const AdminDashboard = () => {
  const { adminData, logout } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [dashboardStats, setDashboardStats] = useState(null)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('adminToken')
      const response = await fetch('/api/admin/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDashboardStats(data.data)
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    }
  }

  // Get current view from URL path
  const getCurrentView = () => {
    const path = location.pathname
    if (path.includes('/compare')) return 'compare'
    if (path.includes('/assessments/')) return 'detail'
    if (path.includes('/assessments')) return 'assessments'
    if (path.includes('/questionnaire')) return 'questionnaire'
    if (path.includes('/users')) return 'users'
    if (path.includes('/tests')) return 'tests'
    return 'dashboard'
  }

  // Component wrappers for routes
  const DashboardView = () => (
    <div className="space-y-6">
      {/* Reader role indicator */}
      {adminData?.role === 'reader' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-900">Vue consultant</p>
            <p className="text-sm text-blue-700 mt-1">
              Vous visualisez uniquement les évaluations de vos clients.
            </p>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Tableau de bord
        </h1>
        <button
          onClick={() => navigate('/admin/assessments')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          {adminData?.role === 'reader' ? 'Voir vos évaluations' : 'Voir toutes les évaluations'}
        </button>
      </div>
      
      {dashboardStats && (
        <DashboardStats 
          stats={dashboardStats}
          role={adminData?.role}
          onViewAssessments={() => navigate('/admin/assessments')}
        />
      )}
    </div>
  )

  const AssessmentsView = () => (
    <div className="space-y-6">
      {/* Reader role indicator */}
      {adminData?.role === 'reader' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-900">Vue consultant</p>
            <p className="text-sm text-blue-700 mt-1">
              Vous visualisez uniquement les évaluations de vos clients.
            </p>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {adminData?.role === 'reader' ? 'Vos évaluations' : 'Évaluations soumises'}
        </h1>
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour au tableau de bord
        </button>
      </div>
      
      <AssessmentsList />
    </div>
  )

  const AssessmentDetailView = () => {
    const { id } = useParams()
    const [assessment, setAssessment] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
      if (id) {
        fetchAssessment(id)
      }
    }, [id])

    const fetchAssessment = async (assessmentId) => {
      try {
        setLoading(true)
        setError(null)
        const token = localStorage.getItem('adminToken')
        const response = await fetch(assessmentApiUrl(assessmentId), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setAssessment(data.data)
        } else if (response.status === 403) {
          // 🔒 Access denied - reader trying to view another consultant's assessment
          setError('access_denied')
        } else if (response.status === 404) {
          setError('not_found')
        } else {
          setError('server_error')
        }
      } catch (error) {
        console.error('Error fetching assessment:', error)
        setError('network_error')
      } finally {
        setLoading(false)
      }
    }

    if (loading) {
      return (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      )
    }

    if (error) {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-8">
            <div className="flex items-center justify-center mb-4">
              <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              {error === 'access_denied' ? 'Accès refusé' : error === 'not_found' ? 'Évaluation introuvable' : 'Erreur'}
            </h2>
            <p className="text-gray-600 text-center mb-6">
              {error === 'access_denied' 
                ? 'Vous n\'avez pas accès à cette évaluation. Seules les évaluations de vos clients sont accessibles.'
                : error === 'not_found'
                ? 'Cette évaluation n\'existe pas ou a été supprimée.'
                : 'Une erreur est survenue lors du chargement de l\'évaluation.'}
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/admin/assessments')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retour à la liste
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            Détails de l'évaluation
          </h1>
          <button
            onClick={() => navigate('/admin/assessments')}
            className="text-blue-600 hover:text-blue-800 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour à la liste
          </button>
        </div>
        
        {assessment && <AssessmentDetail assessment={assessment} />}
      </div>
    )
  }

  const UsersView = () => (
    <div className="space-y-6">
      <UserManagement />
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader 
        adminData={adminData}
        onLogout={logout}
        currentView={getCurrentView()}
      />

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Routes>
          <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardView />} />
          <Route path="/assessments" element={<AssessmentsView />} />
          <Route path="/assessments/:id" element={<AssessmentDetailView />} />
          <Route path="/compare" element={<ComparisonPage />} />
          <Route 
            path="/questionnaire" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'editor']} adminData={adminData}>
                <QuestionnaireManager />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/users" 
            element={
              <ProtectedRoute allowedRoles={['admin']} adminData={adminData}>
                <UsersView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tests" 
            element={
              <ProtectedRoute allowedRoles={['admin']} adminData={adminData}>
                <AdminTestPanel />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
    </div>
  )
}

export default AdminDashboard
